import { CLINIC } from "../lib/content";
import { ALL_CONDITIONS } from "../lib/conditions";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, ArrowUpRight } from "lucide-react";

const IG_HANDLE = "dr.yasminimdad";

function conditionSlug(name) {
  return name.toLowerCase().replace(/[^\w]+/g, "-").replace(/^-|-$/g, "");
}

export default function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="rounded-[1.5rem] p-8 sm:p-10 mb-16 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg,#F58529 0%,#DD2A7B 45%,#8134AF 80%,#515BD4 100%)" }}
        >
          <div className="absolute inset-0 opacity-25 mix-blend-overlay" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.7) 0%, transparent 45%), radial-gradient(circle at 85% 70%, rgba(255,255,255,0.5) 0%, transparent 45%)" }} />
          <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="overline text-white/85 flex items-center gap-2"><Instagram size={14} /> Instagram</p>
              <p className="font-serif text-2xl sm:text-3xl mt-2 leading-tight">Learn from Dr. Yasmin — one reel at a time.</p>
            </div>
            <a
              href={`https://www.instagram.com/${IG_HANDLE}/`}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-ig-follow-btn"
              className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-white text-[#8134AF] pl-2 pr-6 py-2 font-semibold hover:-translate-y-0.5 transition-transform duration-300"
              style={{ boxShadow: "0 20px 40px -14px rgba(0,0,0,0.4)" }}
            >
              <span className="w-11 h-11 rounded-full grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#F58529,#DD2A7B,#8134AF)" }}>
                <Instagram size={18} />
              </span>
              <span className="tracking-tight">Follow @{IG_HANDLE}</span>
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>

        <div className="grid md:grid-cols-[1.4fr_1fr_1fr] gap-12 pb-14">
          <div>
            <p className="font-serif text-3xl">Mom &amp; More Clinic</p>
            <p className="mt-4 text-sm text-primary-foreground/70 max-w-sm leading-relaxed">
              Compassionate obstetrics &amp; gynaecology care by {CLINIC.doctor} — {CLINIC.credentials}.
            </p>
            <a
              href="/#booking"
              className="inline-flex mt-7 items-center rounded-full bg-secondary text-secondary-foreground px-6 py-3 text-sm font-semibold hover:-translate-y-0.5 transition-transform duration-300"
            >
              Book an appointment
            </a>
          </div>

          <div>
            <p className="overline text-secondary mb-5">Quick links</p>
            <ul className="space-y-3 text-sm text-primary-foreground/80">
              <li><a href="/#about" className="hover:text-secondary transition-colors">About Dr. Yasmin</a></li>
              <li><a href="/#reels" className="hover:text-secondary transition-colors">Instagram reels</a></li>
              <li><a href="/#stories" className="hover:text-secondary transition-colors">Patient stories</a></li>
              <li><a href="/#location" className="hover:text-secondary transition-colors">Visit the clinic</a></li>
              <li><a href="/#booking" className="hover:text-secondary transition-colors">Book appointment</a></li>
            </ul>
          </div>

          <div>
            <p className="overline text-secondary mb-5">Contact</p>
            <ul className="space-y-4 text-sm text-primary-foreground/80">
              <li className="flex gap-3"><MapPin size={17} className="text-secondary shrink-0" /><span>{CLINIC.address}</span></li>
              <li className="flex gap-3 items-center"><Phone size={17} className="text-secondary shrink-0" /><a href={`tel:${CLINIC.phone.replace(/\s/g, "")}`} className="hover:text-secondary">{CLINIC.phone}</a></li>
              <li className="flex gap-3 items-center"><Mail size={17} className="text-secondary shrink-0" /><a href={`mailto:${CLINIC.email}`} className="hover:text-secondary">{CLINIC.email}</a></li>
            </ul>
          </div>
        </div>

        {/* Conditions cloud */}
        <div className="border-t border-white/15 pt-12 pb-10">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="overline text-secondary">Care we provide</p>
              <p className="font-serif text-2xl sm:text-3xl mt-2 max-w-xl leading-tight">
                Conditions Dr. Yasmin treats — from PCOS to menopause.
              </p>
            </div>
            <p className="text-xs text-primary-foreground/60 max-w-xs">
              Tap any concern to start a booking. Not sure which fits you? Just book a consultation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="footer-conditions">
            {ALL_CONDITIONS.map((c) => (
              <Link
                key={c}
                to={`/care/${conditionSlug(c)}`}
                className="text-[0.72rem] sm:text-xs px-3 py-1.5 rounded-full border border-white/20 text-primary-foreground/85 hover:bg-secondary hover:text-secondary-foreground hover:border-secondary transition-colors"
              >
                {c}
              </Link>
            ))}
          </div>
        </div>

        <div className="border-t border-white/15 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-primary-foreground/60">
          <p>© {new Date().getFullYear()} Mom &amp; More Clinic. All rights reserved.</p>
          <p>Bengaluru · Karnataka, India</p>
        </div>
      </div>
    </footer>
  );
}
